const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

// The content-id -> content shape stores a plain string today. Because it's just
// a string attribute in DynamoDB, storing HTML instead later needs no schema change.
type ContentRecord = {
  'content-id': string;
  content: string;
};

export async function getContent(contentId: string): Promise<string | null> {
  const res = await fetch(`${API_URL}/content/${encodeURIComponent(contentId)}`);

  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load content "${contentId}"`);

  const data: ContentRecord = await res.json();
  return data.content ?? null;
}

export async function saveContent(
  contentId: string,
  content: string,
  idToken: string,
  exists: boolean
): Promise<void> {
  const url = exists
    ? `${API_URL}/content/${encodeURIComponent(contentId)}`
    : `${API_URL}/content`;
  const method = exists ? 'PUT' : 'POST';
  const body: ContentRecord = { 'content-id': contentId, content };

  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    throw new Error(`Failed to save content "${contentId}"`);
  }
}

export async function deleteContent(contentId: string, idToken: string): Promise<void> {
  const res = await fetch(`${API_URL}/content/${encodeURIComponent(contentId)}`, {
    method: 'DELETE',
    headers: { Authorization: idToken },
  });

  if (!res.ok) {
    throw new Error(`Failed to delete content "${contentId}"`);
  }
}

export type Sponsor = {
  'content-id': string;
  type: 'sponsor';
  name: string;
  imageUrl: string;
  websiteUrl: string;
};

export async function getSponsors(): Promise<Sponsor[]> {
  const res = await fetch(`${API_URL}/sponsors`);
  if (!res.ok) throw new Error('Failed to load sponsors');
  return res.json();
}

export async function saveSponsor(sponsor: Sponsor, idToken: string, exists: boolean): Promise<void> {
  const url = exists
    ? `${API_URL}/content/${encodeURIComponent(sponsor['content-id'])}`
    : `${API_URL}/content`;
  const method = exists ? 'PUT' : 'POST';

  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify(sponsor),
  });

  if (!res.ok) {
    throw new Error('Failed to save sponsor');
  }
}

function resolveContentType(file: File): string {
  if (file.type) return file.type;
  if (file.name.toLowerCase().endsWith('.svg')) return 'image/svg+xml';
  return 'application/octet-stream';
}

export async function uploadSponsorImage(file: File, idToken: string): Promise<string> {
  const contentType = resolveContentType(file);

  const urlRes = await fetch(`${API_URL}/sponsors/upload-url`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ fileName: file.name, contentType }),
  });

  if (!urlRes.ok) throw new Error('Failed to get an upload URL');
  const { uploadUrl, imageUrl } = await urlRes.json();

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });

  if (!putRes.ok) throw new Error('Failed to upload image');
  return imageUrl;
}

export type InvitedUser = {
  email: string;
  firstName: string;
  lastName: string;
  status: string;
  enabled: boolean;
};

export async function listUsers(idToken: string): Promise<InvitedUser[]> {
  const res = await fetch(`${API_URL}/users`, {
    headers: { Authorization: idToken },
  });

  if (!res.ok) throw new Error('Failed to load users');
  return res.json();
}

export async function inviteUser(
  email: string,
  firstName: string,
  lastName: string,
  idToken: string
): Promise<void> {
  const res = await fetch(`${API_URL}/users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: idToken,
    },
    body: JSON.stringify({ email, firstName, lastName }),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message || 'Failed to send invite');
  }
}

export async function removeUser(email: string, idToken: string): Promise<void> {
  const res = await fetch(`${API_URL}/users/${encodeURIComponent(email)}`, {
    method: 'DELETE',
    headers: { Authorization: idToken },
  });

  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.message || 'Failed to remove user');
  }
}
